import IconButton from "../kit/IconButton";
import KitButton from "../kit/Button";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { MdDragIndicator } from "react-icons/md";
import SocialsInput from "./SocialsInput";
import { socialOptions } from "../../util/socials";
import { v4 as uuid } from "uuid";

const PLAYABLE_MEDIA_SOCIAL_IDS = new Set([
  "youtube",
  "spotify",
  "soundcloud",
  "bandcamp",
  "vimeo",
  "apple",
]);

// socialType is stamped when the entry is created from a section button;
// platform covers entries whose pasted link was detected as a playable service.
const isPlayableSocial = (social) =>
  PLAYABLE_MEDIA_SOCIAL_IDS.has(social?.socialType) ||
  PLAYABLE_MEDIA_SOCIAL_IDS.has(social?.platform);

const createBoundedVerticalDragModifier = (containerRef) => ({
  transform,
  activeNodeRect,
}) => {
  const containerRect = containerRef.current?.getBoundingClientRect();
  if (!containerRect || !activeNodeRect) {
    return {
      ...transform,
      x: 0,
    };
  }

  const minY = containerRect.top - activeNodeRect.top;
  const maxY = containerRect.bottom - activeNodeRect.bottom;
  const lowerBound = Math.min(minY, maxY);
  const upperBound = Math.max(minY, maxY);

  return {
    ...transform,
    x: 0,
    y: Math.max(lowerBound, Math.min(transform.y, upperBound)),
  };
};

const normalizeSocial = (social = {}) => ({
  ...(typeof social === "object" ? social : { platform: "", link: "" }),
  id: social?.id || uuid(),
});

const SortableSocialRow = ({ children, id }) => {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  return (
    <div
      ref={setNodeRef}
      className="group flex items-start gap-kit-8 rounded-kit-lg"
      style={{
        position: "relative",
        zIndex: isDragging ? 10 : undefined,
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <IconButton
        label="drag to reorder social link"
        className="mt-kit-8 cursor-grab touch-none opacity-60"
        icon={<MdDragIndicator />}
        {...attributes}
        {...listeners}
      />
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
};

const SocialsHandler = forwardRef(({ value, onChange, defaultValue }, ref) => {
  const defValue = defaultValue || value || [
    {
      platform: "",
      link: "",
    },
  ]
  const [socials, setSocials] = useState(() => defValue.map(normalizeSocial));
  const playableListRef = useRef(null);
  const linkListRef = useRef(null);
  const playableDragModifiers = useMemo(
    () => [createBoundedVerticalDragModifier(playableListRef)],
    []
  );
  const linkDragModifiers = useMemo(
    () => [createBoundedVerticalDragModifier(linkListRef)],
    []
  );
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 120, tolerance: 6 },
    })
  );

  useEffect(() => {
    if (value === undefined) return;

    if (JSON.stringify(value) !== JSON.stringify(socials)) {
      setSocials(value?.length > 0 ? value.map(normalizeSocial) : []);
    }
  }, [value]);

  // useEffect(() => {

  //   alert(`value ${JSON.stringify(socials,null,2)}`);
  // },[socials])

  const inputRefs = useRef([]);

  // Ref mechanism for parent to interact with SocialsHandler
  useImperativeHandle(ref, () => ({
    getValue: () => {
      // console.log("returning socials ", socials);
      return socials || [];
    }, // Provide current value to parent
    setValue: (newValue) => {
      const nextValue = (newValue || []).map(normalizeSocial);
      setSocials(nextValue);
      onChange && onChange(nextValue);
    },
    reset: () => onChange && onChange([]), // Reset all socials
    addSocial: () => {
      const newValue = [...(socials || []), normalizeSocial()];
      setSocials(newValue);
      onChange && onChange(newValue);
    },
  }));

  // const handleDelete = (indexToDelete) => {
  //   const newSocials = socials.filter((_, index) => index !== indexToDelete);
  //   setSocials([...newSocials]); // Ensure a new array reference is used
  //   onChange && onChange([...newSocials]);
  // };

  const handleDelete = (indexToDelete) => {
    const newSocials = socials.filter((_, index) => index !== indexToDelete);
    setSocials(newSocials);
    // onChange && onChange(newSocials.filter(social => social.platform || social.link));
    onChange && onChange(newSocials);
  };

  const handleAddSocialOption = (option) => () => {
    // Set link to empty string with socialType to track which button was clicked
    const newSocials = [
      {
        id: uuid(),
        platform: "",
        link: "",
        socialType: option.id, // Track which social button was clicked
        linkPlaceholder: option.patterns[0] || 'www.example.com/', // Store placeholder for display
      },
      ...socials,
    ];

    //TODO focus the input

    setSocials(newSocials);

    setTimeout(() => {
      inputRefs.current[0]?.linkInput()?.focus();
    }, 100);
  };

  const handleChange = (index) => (v) => {
    // console.log("v", v);
    // alert(`v ${JSON.stringify(v, null, 2)}`);
    const newValue = [...socials];
    newValue[index] = {
      ...newValue[index],
      ...v,
      id: newValue[index]?.id || uuid(),
    };
    setSocials(newValue);
    onChange && onChange(newValue);
  }
  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    const oldIndex = socials.findIndex((social) => social.id === active.id);
    const newIndex = socials.findIndex((social) => social.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const newSocials = arrayMove(socials, oldIndex, newIndex);
    setSocials(newSocials);
    onChange && onChange(newSocials);
  };

  const playableMediaOptions = socialOptions.filter((option) =>
    PLAYABLE_MEDIA_SOCIAL_IDS.has(option.id)
  );
  const socialLinkOptions = socialOptions.filter(
    (option) => !PLAYABLE_MEDIA_SOCIAL_IDS.has(option.id)
  );
  const renderSocialOptionButton = (option) => (
    <div key={option.id} data-social-option={option.id} className="contents">
      <KitButton variant="secondary" size="lg" aria-label={`add ${option.id}`} title={`add ${option.id}`} icon={<option.rawIcon size={24} />} onClick={handleAddSocialOption(option)} />
    </div>
  );

  // Entries keep their master index so change/delete/refs map to `socials`.
  const allEntries = (socials || []).map((social, index) => ({ social, index }));
  const playableEntries = allEntries.filter(({ social }) => isPlayableSocial(social));
  const linkEntries = allEntries.filter(({ social }) => !isPlayableSocial(social));

  const renderSocialList = (entries, containerRef, modifiers) => {
    if (entries.length === 0) return null;

    return (
      <DndContext
        collisionDetection={closestCenter}
        modifiers={modifiers}
        onDragEnd={handleDragEnd}
        sensors={sensors}
      >
        <SortableContext
          items={entries.map(({ social }) => social.id)}
          strategy={verticalListSortingStrategy}
        >
          <div
            ref={containerRef}
            className="mt-kit-16 flex flex-col gap-kit-20"
          >
            {entries.map(({ social: v, index: i }) => {
              return (
                <SortableSocialRow key={v.id} id={v.id}>
                  <SocialsInput
                    ref={(el) => (inputRefs.current[i] = el)}
                    z={204 + socials.length - 1 - i}
                    value={v}
                    onChange={handleChange(i)}
                    onDelete={() => handleDelete(i)}
                    linkError={v.linkerror}
                    platformError={v.platformerror}
                  />
                </SortableSocialRow>
              );
            })}
          </div>
        </SortableContext>
      </DndContext>
    );
  };

  return (
    <div>
      {/* Social Options as Buttons */}
      <div className="mb-kit-16 flex flex-col gap-kit-16">
        <div>
          <div className="ui-label mb-kit-8">
            playable media
          </div>
          <div className="inyc-help mb-[calc(0.75rem+5px)]">
            paste an individual song or video link to embed playable media directly on your event post. links to account pages do not embed
          </div>
          <div className="flex flex-wrap gap-kit-8 md:justify-start justify-center">
            {playableMediaOptions.map(renderSocialOptionButton)}
          </div>
          {renderSocialList(playableEntries, playableListRef, playableDragModifiers)}
        </div>

        <div>
          <div className="ui-label mb-kit-8">
            social links
          </div>
          <div className="inyc-help mb-[calc(0.75rem+5px)]">
            paste the url link for the account
          </div>
          <div className="flex flex-wrap gap-kit-8 md:justify-start justify-center">
            {socialLinkOptions.map(renderSocialOptionButton)}
          </div>
          {renderSocialList(linkEntries, linkListRef, linkDragModifiers)}
        </div>
      </div>
    </div>
  );
});

export default SocialsHandler;
